import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Container, Card, Form, Button, Alert } from "react-bootstrap";
import { useRegisterStudent } from "../hooks/useRegisterStudent";
import { useTenant } from "../../../app/providers/TenantProvider";
import { NewPasswordFields } from "../../../components/NewPasswordFields";
import { ConsentCheckbox } from "../../../app/privacy/ConsentCheckbox";

function RegisterStudent() {
  const { slug } = useParams();
  const { tenantName } = useTenant();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordValid, setPasswordValid] = useState(false);
  const [consent, setConsent] = useState(false);
  const [done, setDone] = useState(false);
  const [frozen, setFrozen] = useState(false);

  const { mutate, isPending, isError, error } = useRegisterStudent();

  function handleSubmit(e) {
    e.preventDefault();
    mutate(
      { slug, student: { name, email, password } },
      {
        onSuccess(data) {
          setFrozen(Boolean(data?.frozen));
          setDone(true);
        },
      }
    );
  }

  if (done) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center min-vh-100"
        style={{ padding: "1rem" }}
      >
        <Card className="w-100 text-center shadow-sm" style={{ maxWidth: 440 }}>
          <Card.Body className="p-4">
            {frozen ? (
              <>
                <h5>Registro recibido — en espera de capacidad</h5>
                <p className="text-muted mt-2">
                  Tu academia alcanzó el máximo de estudiantes de su plan. Tu
                  cuenta quedó <strong>en espera</strong> y se activará
                  automáticamente cuando amplíe su capacidad. Igual revisa tu
                  correo para activar tu cuenta.
                </p>
              </>
            ) : (
              <>
                <h5>¡Registro exitoso!</h5>
                <p className="text-muted mt-2">
                  Revisa tu correo electrónico para activar tu cuenta.
                </p>
              </>
            )}
            <Link
              to={`/${slug}/login`}
              className="btn btn-outline-primary mt-2"
            >
              Ir a iniciar sesión
            </Link>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const fieldErrors = error?.response?.data?.field_errors ?? {};
  const globalError =
    isError && !Object.keys(fieldErrors).length
      ? error?.response?.data?.message ||
        "Error al registrar. Verifica los datos."
      : null;

  return (
    <Container
      className="d-flex justify-content-center align-items-center min-vh-100"
      style={{ padding: "1rem" }}
    >
      <Card className="w-100 shadow-sm" style={{ maxWidth: 440 }}>
        <Card.Body className="p-4">
          <h4 className="mb-1">
            {tenantName ? `Regístrate en ${tenantName}` : "Crear cuenta"}
          </h4>
          <p className="text-muted mb-4">
            Regístrate como estudiante para acceder a tu academia.
          </p>

          {globalError && <Alert variant="danger">{globalError}</Alert>}

          <Form onSubmit={handleSubmit} noValidate>
            <Form.Group className="mb-3" controlId="studentName">
              <Form.Label>Nombre completo</Form.Label>
              <Form.Control
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Luis Gómez"
                isInvalid={Boolean(fieldErrors["student.name"])}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors["student.name"]}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="studentEmail">
              <Form.Label>Correo electrónico</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                placeholder="luis@correo.com"
                isInvalid={Boolean(fieldErrors["student.email"])}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors["student.email"]}
              </Form.Control.Feedback>
            </Form.Group>

            <NewPasswordFields
              value={password}
              onChange={setPassword}
              onValidityChange={setPasswordValid}
              passwordId="studentPassword"
              errorText={fieldErrors["student.password"]}
            />

            <ConsentCheckbox checked={consent} onChange={setConsent} />

            <Button
              type="submit"
              variant="primary"
              className="w-100"
              disabled={isPending || !consent || !passwordValid}
            >
              {isPending ? "Registrando…" : "Crear cuenta"}
            </Button>
          </Form>

          <p
            className="text-center mt-3 mb-0"
            style={{ fontSize: "0.875rem" }}
          >
            ¿Ya tienes cuenta?{" "}
            <Link to={`/${slug}/login`}>Iniciar sesión</Link>
          </p>
        </Card.Body>
      </Card>
    </Container>
  );
}

export { RegisterStudent };
